/**
 * «Who is looking at this screen» — role, platform owner, `/access/me` — as
 * one value for both shells (#1645, review #1678). It is what `decideScreen`,
 * `dashboardFor` and the whole-base assistant entry are asked with.
 *
 * - Portal: the PermissionProvider's own values.
 * - Bitrix24 iframe: the session's role, never a platform owner, and the
 *   iframe's own `/access/me` (`useEmbeddedAccess`). Provided by
 *   `EmbeddedScreenSubjectProvider`, which the embedded layout mounts under
 *   the consent and access-request gates — the read needs the session they
 *   settle — and above the assistant drawer and the dashboard, the two that
 *   decide by it.
 *
 * `null` outside both: nothing to decide by, and every caller keeps what it
 * did before E1.5.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useFeatureKnownOff } from '@/hooks/useFeatureKnownOff';
import { useOptionalPermissions } from '@/contexts/PermissionContext';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useEmbeddedAccess } from '@/hooks/useEmbeddedAccess';
import {
  canOpenPath,
  type AccessState,
  type ScreenSubject,
} from '@/lib/route-permissions';

const EmbeddedScreenSubjectContext = createContext<ScreenSubject | null>(null);

export function EmbeddedScreenSubjectProvider({ children }: { children: ReactNode }) {
  const { role } = useEmbeddedOrg();
  const access = useEmbeddedAccess(role);
  const subject = useMemo<ScreenSubject>(
    () => ({ globalRole: role, isOwner: false, access }),
    [role, access]
  );
  return (
    <EmbeddedScreenSubjectContext.Provider value={subject}>
      {children}
    </EmbeddedScreenSubjectContext.Provider>
  );
}

/** No answer is asked without the provider: the role decides (`AccessState`). */
const ACCESS_NOT_ASKED: AccessState = Object.freeze({ status: 'failed' });

/**
 * The subject inside the Bitrix24 iframe, for code that only renders there
 * (the embedded dashboard). Without the provider — a test, a route mounted
 * outside the layout — the session's role alone decides.
 */
export function useEmbeddedScreenSubject(): ScreenSubject {
  const { role } = useEmbeddedOrg();
  const subject = useContext(EmbeddedScreenSubjectContext);
  return useMemo(
    () => subject ?? { globalRole: role, isOwner: false, access: ACCESS_NOT_ASKED },
    [subject, role]
  );
}

export function useScreenSubject(): ScreenSubject | null {
  const permissions = useOptionalPermissions();
  const embedded = useContext(EmbeddedScreenSubjectContext);
  return useMemo(
    () =>
      permissions
        ? {
            globalRole: permissions.globalRole,
            isOwner: permissions.isOwner,
            access: permissions.access,
          }
        : embedded,
    [permissions, embedded]
  );
}

/**
 * «May a link here lead to this path» for the subject of either shell
 * (`canOpenPath`, #1677): a dashboard asks it before it offers a drill-down,
 * so a grant holder is not sent to a screen the gate bounces it from.
 */
export function useCanOpenPath(): (path: string) => boolean {
  const subject = useScreenSubject();
  // The route's feature flag too: a link to a switched-off feature leads to
  // «Feature Not Available» — in the old role dashboards as on the unified
  // card (#2568).
  const featureOff = useFeatureKnownOff();
  return useCallback(
    (path: string) => canOpenPath(path, subject, { featureOff }),
    [subject, featureOff]
  );
}
